function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function pauseElementBasedMedia(container) {
  if (!container) return;
  container.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  container.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  container.querySelectorAll('video').forEach((video) => video.pause());
  container.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

class MarqueeComponent extends HTMLElement {
  constructor() {
    super();
  }
  
  connectedCallback(){
    const initialize = () => {
      this.init = null;
      this.initBounds = getColumnsSize() * 2;
      this.mainBounds = this.dataset.rotation ? this.getBoundingClientRect().height : this.getBoundingClientRect().width;
      this.dataset.direction === 'forward' ? setOnMarquee(this.initBounds * -1, this.initBounds) : setOnMarquee(0, this.initBounds);
      if(this.dataset.pauseOnHover === 'true'){
        this.addEventListener('mouseenter', setOnPause.bind(this));
        this.addEventListener('mouseleave', setOnResume.bind(this));
      }
    }
  
    const setTabIndexing = () => {
      const focusables = this.querySelectorAll('[class*="cloned"] a', '[class*="cloned"] button');
      focusables.forEach(element => {
        element.tabIndex = -1;
        element.setAttribute('aria-hidden', 'true');
      });
    }
  
    const setOnMarquee = (pos, cycle) => {
      const speed = parseFloat(this.dataset.speed);
      let cycleCount = cycle;
      let container = this.querySelector('.marquee-component');
      container.classList.remove('marquee-component--h-scroll', 'marquee-component--v-scroll');
      setOnPause();
      this.init = setInterval(frame.bind(this), speed);
  
      function frame(){
        if(cycleCount <= 1){
          setOnPause();
          const value = 0 + 'px';
          if(this.dataset.rotation){
            container.style.setProperty('--mrq-top', value);
          }
          if(!this.dataset.rotation){
            container.style.setProperty('--mrq-left', value);
          }
          this.dataset.direction === 'forward' ? setOnMarquee(this.initBounds * -1, this.initBounds) : setOnMarquee(0, this.initBounds);
          this.pos = pos;
          return;
        }
        this.dataset.direction === 'forward' ? pos++ : pos--;
        cycleCount--;
        this.pos = pos;
        this.cycleCount = cycleCount;
        const value = pos + 'px';
        if(this.dataset.rotation) container.style.setProperty('--mrq-top', value);
        if(!this.dataset.rotation) container.style.setProperty('--mrq-left', value);
      }
    }
  
    const setOnPause = () => {
      clearInterval(this.init);
    }
  
    const setOnResume = () => {
      setOnMarquee(this.pos, this.cycleCount);
    }
  
    const getColumnsSize = () => {
      let widths = 0;
      const columns = this.querySelectorAll('.marquee-component_col:not(.clonedBefore):not(.clonedAfter)');
      columns.forEach(column => {
        const bounds = column.getBoundingClientRect();
        widths += this.dataset.rotation ? bounds.height : bounds.width;
      });
      setTabIndexing();
      return widths;
    }
    this.initLoad = setTimeout(initialize.bind(this), 2000);
    document.addEventListener('DOMContentLoaded', function(){this.initLoad;});
  }
}
if(!customElements.get('marquee-component')) customElements.define('marquee-component', MarqueeComponent);


// 🧪 Problem 1 — Discount Engine (Shopify Mindset)
// Scenario:

// You are building a discount system for an eCommerce store.

// Rules:
// If cart total ≥ 100 → 20% discount
// If cart total ≥ 50 → 10% discount
// Otherwise → No discount

// let total = 50;
// let discount = 0;
// let discountedPrice;
// function applyDiscount(total) {
//   if (total >= 100) {
//       discount = total * 0.2;
//       discountedPrice = total - discount;
//   } else if(total >= 50) {
//       discount = total * 0.1;
//       discountedPrice = total - discount;
//   } else {
//       discountedPrice = total - discount;
//   };
// };

// applyDiscount(total);

// if  (total >= 100){
//   console.log(`Your total is ${total}$ and discount is 20%. Your discounted price is ${discountedPrice}$.`); 
// } else if (total >= 50){
//   console.log(`Your total is ${total}$ and discount is 10%. Your discounted price is ${discountedPrice}$.`); 
// } else {
//   console.log(`Your total is ${total}$ and discount is 0%. Your discounted price is ${discountedPrice}$.`); 
// };

// ********************************************
// 🎯 DRILL SET 1 — IF / ELSE (Decision Mastery)
// ********************************************

// 🧪 Problem 2 — User Role Access
// Scenario:

// You are controlling access in an admin panel.

// Rules:
// "admin" → Full access
// "editor" → Edit access
// "viewer" → Read-only
// Anything else → No access

// const role = 'admin';
// let access;
// function checkAccess(role) {
//   if (role === 'admin') {
//     return access = 'Full';
//   } else if (role === 'editor') {
//     return access = 'Edit';
//   } else if (role === 'viewer') {
//     return access = 'Read-only';
//   } else {
//     return access = 'No';
//   };
// };

// checkAccess(role);

// console.log(`Your role is ${role} and you have ${access} access.`);

// 🧪 Problem 3 — Smart Shipping
// Scenario:

// Shipping cost depends on location + price.

// Rules:
// If price ≥ 100 → Free shipping
// Else if country is "USA" → $10
// Else → $25

// const price = 100;
// let country = "USA";
// let shippingCost;

// function claculateShipping(price, country) {
//   if (price >= 100) {
//     return shippingCost = 'Free';
//   } else if (country === "USA") {
//     return shippingCost = '10$';
//   } else {
//     return shippingCost = '25$';
//   };
// };

// claculateShipping(price, country);

// console.log(`Your price is ${price}$ and your country is ${country} and your shipping cost is ${shippingCost}`);


// ********************************************
// 🎯 DRILL SET 2 — TERNARY OPERATOR (Cleaner Code Thinking)
// ********************************************

// 🧪 Problem 4 — Login Status
// Task:
// isLoggedIn = true

// "Welcome back" OR "Please login"
// ⚠️ Constraint:

// 👉 Must use ternary only

// const isLoggedIn = 'false';
// (isLoggedIn === 'true') ? console.log('Welcome back') : console.log('Please login');


// 🧪 Problem 5 — Stock Checker
// Scenario:

// If product stock > 0 → "In Stock"
// Else → "Out of Stock"

// Task:
// checkStock(stock)

// const productStock = 1;
// (productStock > 0) ? console.log('In Stock') : console.log('Out of Stock');

// 🧪 Problem 6 — Even or Odd (Ternary Version)

// Solve it using ternary.

// const number = 1;
// (number % 2 === 0) ? console.log(`Number ${number} is Even`) : console.log(`Number ${number} is Odd`);

// 🧪 Problem 7 — Age Category
// Rules:
// age ≥ 18 → "Adult"
// Else → "Minor"

// 👉 One-line ternary only.

// const age = 18;
// (age >= 18) ? console.log(`You are ${age} years old, so you are an Adult`) : console.log(`You are ${age} years old, so you are a Minor`);


// *********************************************
// 🎯 DRILL SET 3 — SWITCH (Structured Decisions)
// *********************************************

// 🧪 Problem 8 — Day of Week
// Task:

// Input:
// 1 → Monday  
// 2 → Tuesday  
// ...
// 7 → Sunday

// Function:
// getDay(dayNumber)

// const dayNumber = 1;
// let day;
// function getDay(dayNumber) {
//   switch (dayNumber) {
//     case 1:
//       day = 'Monday';
//       break;
//     case 2:
//       day = 'Tuesday';
//     case 3:
//       day = 'Wednesday';
//     case 4:
//       day = 'Thursday';
//     case 5:
//       day = 'Friday';
//     case 6:
//       day = 'Saturday';
//     case 7:
//       day = 'Sunday';
//     default:
//       day = 'Invalid day';
//       break;
//   };
// };
// getDay(dayNumber);
// console.log(`The day is ${day}`);


// 🧪 Problem 9 — Theme Mode
// Scenario:

// Switch UI theme

// Rules:
// "light" → "Light Mode Activated"
// "dark" → "Dark Mode Activated"
// "system" → "System Default"
// default → "Unknown Mode"

// const mode = 'system';
// let themeMode;
// function selectMode(mode) {
//   switch (mode) {
//     case 'light':
//       themeMode = 'Light Mode Activated';
//       break;
//     case 'dark':
//       themeMode = 'Dark Mode Activated';
//       break;
//     case 'system':
//       themeMode = 'System Default';
//       break;
//     default:
//       themeMode = 'Unknown Mode';
//       break;
//   };
// };

// selectMode(mode);
// console.log(themeMode);


// 🧪 Problem 10 — Payment Status
// Scenario:

// Order system

// Status:
// "pending"
// "paid"
// "failed"
// "refunded"
// Task:

// Return meaningful messages for each.

// const paymentStatus = 'pending';
// let orderStatus;

// function checkOrderStatus(paymentStatus) {
//   switch (paymentStatus) {
//     case 'pending':
//       orderStatus = "Your payment status is pending that's why your order can not be shipped";
//       break;
//     case 'paid':
//       orderStatus = "Your payment status is paid, Your order will be shipped in next 3 to 5 working days";
//       break;
//     case 'failed':
//       orderStatus = "Your order can not be fulfilled beause the payment status is failed";
//       break;
//     case 'refunded':
//       orderStatus = "your order can not be fulfilles because your money is refunded";
//       break;
//     default:
//       orderStatus = "We can not fullfill your order due to some internal errors";
//       break;
//   };
// };

// checkOrderStatus(paymentStatus);
// console.log(orderStatus);


//*********************************************************
// 🎯 DRILL SET 2 — CONTROL FLOW MASTERY (REAL DEV THINKING)
// Each problem is slightly more “real-world” and structured.
//*********************************************************


// 🧪 Problem 1 — Coupon Validator (Ecommerce Core Logic)
// Scenario:

// You are validating a discount coupon.

// Rules:
// "SAVE10" → 10% discount
// "SAVE20" → 20% discount
// "FREESHIP" → Free shipping
// Anything else → Invalid coupon
// Task:
// validateCoupon(code)
// Expected:
// Return an object, not a string

// Example:

// validateCoupon("SAVE10")
// → { valid: true, discount: 10, freeShipping: false }
// ⚠️ Guidance:
// Use switch or if/else
// Must return structured data (important real-world pattern)


// function validateCoupon(code) {
//   if (code === 'SAVE10') {
//     return { valid: true, discount: 10, freeShipping: false };
//   } else if (code === 'SAVE20') {
//     return { valid: true, discount: 20, freeShipping: false };
//   } else if (code === 'FREESHIP') {
//     return { valid: true, discount: 0, freeShipping: true };
//   } else {
//     return { valid: false, discount: 0, freeShipping: false };
//   };
// };

// const result = validateCoupon('SAVE10');
// console.log(`Your coupon is ${result.valid ? "valid" : "not valid"} and you got ${result.discount > 0 ? result.discount : 0}% discount and you have ${result.freeShipping ? "free shipping" : "no free shipping"}.`);


// 🧪 Problem 2 — Cart Quantity Pricing
// Scenario:

// Bulk pricing system

// Rules:
// quantity ≥ 100 → price = $5 each
// quantity ≥ 50 → price = $7 each
// otherwise → $10 each
// Task:
// calculatePrice(quantity)
// Expected:

// Return:

// {
//   quantity: 60,
//   unitPrice: 7,
//   total: 420
// }
// ⚠️ Guidance:
// Think top-down condition
// Return object (like Shopify cart logic)

// function calculatePrice(quantity) {
//   if (quantity >= 100) {
//     unitPrice = 5;
//     total = quantity * unitPrice;
//   } else if (quantity >= 50) {
//     unitPrice = 7;
//     total = quantity * unitPrice;
//   } else {
//     unitPrice = 10;
//     total = quantity * unitPrice;
//   }
//   return {
//     quantity,
//     unitPrice,
//     total
//   };
// };

// const result = calculatePrice(51);
// console.log(`You ordered ${result.quantity} products and each product price is ${unitPrice}$ and your total is ${result.total}$.`);


// 🧪 Problem 3 — Password Strength Checker
// Rules:
// length < 6 → "Weak"
// length 6–10 → "Medium"
// length > 10 → "Strong"
// Task:
// checkPassword(password)
// ⚠️ Guidance:
// Use .length
// No global variables

// function checkPassword(password) {
//   if (password.length > 0 && password.length < 6) {
//     passwordStrength = "Weak";
//   } else if (password.length >= 6 && password.length <= 10) {
//     passwordStrength = "Medium";
//   } else if (password.length > 10) {
//     passwordStrength = "Strong";
//   } else {
//     passwordStrength = "Invalid";
//   };
//   return {passwordStrength};
// };

// const result = checkPassword("");
// console.log(`Your password is ${result.passwordStrength}`);



// 🧪 Problem 4 — Feature Flag System
// Scenario:

// Enable/disable features

// Rules:
// "beta" → enabled
// "pro" → enabled
// "free" → limited
// others → disabled
// Task:
// getFeatureAccess(plan)

// function getFeatureAccess(plan) {
//   if (plan === 'beta') {
//     access = 'enabled';
//   } else if (plan === 'pro') {
//     access = 'enabled';
//   } else if (plan === 'free') {
//     access = 'limited';
//   } else {
//     access = 'disabled';
//   };
//   return {access};
// };
// const result = getFeatureAccess('beta');
// console.log(`Your features access is ${result.access}.`);


// 🧪 Problem 5 — Order Status Mapper
// Scenario:

// Backend → UI mapping

// Input:
// "processing", "shipped", "delivered", "cancelled"
// Output:
// "processing" → "Your order is being prepared"
// "shipped" → "Your order is on the way"
// "delivered" → "Delivered successfully"
// "cancelled" → "Order cancelled"
// ⚠️ Guidance:
// Use switch
// Must return string

// function orderProcessing(status) {
//   switch (status) {
//     case 'processing':
//       orderStatus = 'Your order is being prepared';
//       break;
//     case 'shipped':
//       orderStatus = 'Your order is on the way';
//       break;
//     case 'delivered':
//       orderStatus = 'Delivered successfully';
//       break;
//     case 'cancelled':
//       orderStatus = 'Order cancelled';
//       break;
//     default:
//       orderStatus = 'Internal error';
//       break;
//   };
//   return {orderStatus};
// };

// const result = orderProcessing('processing');
// console.log(result.orderStatus);


// 🧪 Problem 6 — Age Pricing System
// Rules:
// age < 5 → Free
// 5–18 → $5
// 19–60 → $10
// 60+ → $7
// Task:
// getTicketPrice(age)


// function getTicketPrice(age) {
//   if (age < 5) {
//     ticketPrice = 0;
//   } else if (age >= 5 && age <= 18) {
//     ticketprice = 5;
//   } else if (age >= 19 && age <= 60 ) {
//     ticketPrice = 10;
//   } else if (age > 60) {
//     ticketPrice = 7;
//   };
//   return {
//     age,
//     ticketPrice
//   };
// };

// const result = getTicketPrice(70);
// console.log(`Your age is ${result.age} and your ticket price is ${result.ticketPrice > 0 ? '$' + result.ticketPrice : 'free'}.`);



// 🧪 Problem 7 — Inventory Status
// Rules:
// stock = 0 → "Out of Stock"
// stock 1–10 → "Low Stock"
// stock > 10 → "In Stock"

// function getInventoryStatus(stock) {
//   if (stock === 0) {
//     stockStatus = 'Out of stock';
//   } else if (stock >= 1 && stock <= 10) {
//     stockStatus = 'Low Stock';
//   } else if (stock > 10) {
//     stockStatus = 'In Stock';
//   } else {
//     stockStatus = 'Invalid Stock';
//   };
//   return {stockStatus};
// };

// const result = getInventoryStatus(12);
// console.log(result.stockStatus);




// 🧪 Problem 1 — Price Pipeline (Function Chaining)
// Scenario:

// You are building a checkout system.

// Requirements:
// Step 1:

// Create a function:

// getDiscount(price)
// ≥ 100 → 20%
// ≥ 50 → 10%
// else → 0
// Step 2:

// Create:

// getShipping(price)
// ≥ 100 → Free
// else → $10
// Step 3:

// Create:

// getFinalPrice(price)

// 👉 MUST:

// Call both functions
// Return final price after discount + shipping
// ⚠️ Guidance:
// Use function declaration for main logic
// Break into 3 functions minimum


// function getDiscount(price) {
//   let discount;
//   if (price >= 100) {
//     discount = 0.2;
//   } else if (price >= 50) {
//     discount = 0.1;
//   } else {
//     discount = 0;
//   };
//   return discount;
// };

// function getShipping(price) {
//   let shipping;
//   if (price >= 100 ) {
//     shipping = 0;
//   } else {
//     shipping = 10;
//   };
//   return shipping;
// };

// function getFinalPrice(price) {
//   const finalShipping = getShipping(price);
//   const finalDiscount = getDiscount(price);
//   const discountedPrice = price - ((price * finalDiscount));
//   console.log(`Your price was ${price}$ and you got ${finalDiscount * 100}% discount and your shipping is ${finalShipping > 0 ? finalShipping : 'free'} and your final price is ${discountedPrice}$.`)
// }
// getFinalPrice(100);


// 🧪 Problem 2 — User Formatter (Function Expression)
// Scenario:

// Format user data

// Task:

// Create a function expression:

// const formatUser = function(user) {}

// Input:

// {
//   name: "john",
//   age: 20
// }

// Output:

// "John (20 years old)"
// ⚠️ Guidance:
// Capitalize first letter
// Use string formatting

// const formatUser = function(user) {
//   const capitalizedName = user.name.charAt(0).toUpperCase() + user.name.slice(1);
//   return `${capitalizedName} (${user.age} years old)`;
// };

// const user = {
//   name: "gufran",
//   age: 21
// };

// console.log(formatUser(user));


// 🧪 Problem 3 — Quick Math (Arrow Functions)
// Task:

// Create 3 arrow functions:

// add(a, b)
// multiply(a, b)
// isEven(num)
// ⚠️ Constraint:
// Must be one-line arrow functions

// const addNumbers = (a, b) => console.log(a + b);
// const multiplyNumbers = (a, b) => console.log(a * b);
// const isEven = (num) => num % 2 === 0 ? console.log(`${num} is even.`) : console.log(`${num} is not even.`);

// addNumbers(5, 3);
// multiplyNumbers(5, 3);
// isEven(2);


// 🧪 Problem 4 — Order Processor (Function Calling Function)
// Scenario:

// Order system

// Step 1:
// function validateOrder(amount)
// amount > 0 → true
// else → false
// Step 2:
// function processOrder(amount)

// 👉 MUST:

// Call validateOrder
// If invalid → return "Invalid order"
// Else → "Order processed"
// ⚠️ Guidance:
// Focus on function communication

// const validateOrder = (amount) => amount > 0 ? true : false;
// const processOrder = function(amount) {
//   if (validateOrder(amount) === true) {
//     return 'Order proccessed';
//   } else {
//     return 'Invalid Order';
//   };
// };

// console.log(processOrder(100));




// 🧪 Problem 5 — Product Label System (Mixed Functions)
// Scenario:

// Ecommerce product badge

// Step 1 (Arrow):
// getCategory(price)
// < 20 → "Budget"
// 20–100 → "Standard"

// 100 → "Premium"

// Step 2 (Declaration):
// getLabel(product)

// Input:

// { name: "Shirt", price: 120 }

// Output:

// "Shirt is a Premium product"
// ⚠️ Guidance:
// Combine function types
// One function calls another

// const getCategory = (price) => {
//   if (price < 20) {
//     return 'Budget';
//   } else if (price >= 20 && price <= 100) {
//     return 'Standard';
//   } else {
//     return 'Premium';
//   };
// };

// function getLabel(product) {
//   return `${product.name} is a ${getCategory(product.price)} product`;
// }

// const product = {
//   name: "Shirt",
//   price: 120
// }

// console.log(getLabel(product));



// **************************************
// 🎯 MASTER DRILL SET — “SYSTEM THINKING”
//***************************************


// 🧪 Problem 1 — Smart Cart Summary (Core System)
// Scenario:

// You are building a cart summary system.

// Requirements:

// Create:

// function getCartSummary(price)
// Logic:
// Discount:
// ≥ 100 → 20%
// ≥ 50 → 10%
// Shipping:
// ≥ 100 → Free
// else → $10
// Output:
// {
//   original: 120,
//   discount: 24,
//   shipping: 0,
//   total: 96
// }
// ⚠️ Guidance:
// Break into 3 functions minimum
// Use function calling

// function getDiscount(price) {
//   if (price >= 100) {
//     return 0.2;
//   } else if (price >= 50) {
//     return 0.1;
//   };
//   return 0;
// };

// const getShipping = (price) => price >= 100 ? 0 : 10;

// function getFinalPrice(price) {
//   const discount = price * getDiscount(price);
//   const shipping = getShipping(price);
//   const finalPrice = (price - discount) + shipping;

//   return {
//     original: price,
//     discount: discount,
//     shipping: shipping,
//     total: finalPrice
//   };
// };

// console.log(getFinalPrice(100));



// 🧪 Problem 2 — Username Formatter (String + Function Expression)
// Task:
// const formatUsername = function(name)
// Rules:
// Trim spaces
// Capitalize first letter
// If empty → return "Invalid name"

// const formatUsername = function(name) {
//   name = name.trim();
//   if (name.length === 0) {
//     return "Invalid name";
//   }
//   const capitalizedUserName = name.charAt(0).toUpperCase() + name.slice(1);
//   return capitalizedUserName;
// };

// console.log(formatUsername('ghufran Afzal'));


// 🧪 Problem 3 — Bulk Order Checker (Loop + Condition)
// Task:
// function countExpensiveItems(prices)

// Input:

// [10, 50, 200, 150, 30]
// Rule:
// Count how many items > 100

// function countExpensiveItems(prices) {
//   let count = 0;
//   for (let i = 0; i < prices.length; i++) {
//     if (prices[i] > 100) {
//       count++;
//     };
//   };
//   return count;
// };

// const prices = [10, 50, 200, 150, 30];
// console.log(countExpensiveItems(prices));



// 🧪 Problem 4 — Even Numbers Extractor (Loop + Array)
// Task:
// function getEvenNumbers(numbers)

// Return only even numbers

// function getEvenNumbers(numbers) {
//   let evenNumbers = [];
//   for (let i = 0; i < numbers.length; i++) {
//     if (numbers[i] % 2 === 0) {
//       evenNumbers.push(numbers[i]);
//     };
//   };
//   return evenNumbers;
// };
// const numbers = [2, 5, 6, 8, 9, 11];
// console.log(getEvenNumbers(numbers));




// 🧪 Problem 5 — Order Status Engine (Switch)
// Input:
// "pending", "paid", "shipped", "cancelled"
// Task:

// Return proper message using switch

// function getOrderStatus(orderStatus) {
//   switch (orderStatus) {
//     case 'pending':
//       return "Your payment is not received yet so the order is pending";
//     case 'paid':
//       return "Your payment is received and your order will be shipped";
//     case 'shipped':
//       return "Your order is shipped and will be delivered in next 3 to 5 working days"
//     case 'cancelled':
//       return "Your order cannot be fulfilled because it has been cancelled";
//     default:
//       return "Invalid order status";
//   };
// }

// console.log(getOrderStatus('pending'));



// 🧪 Problem 6 — Login Guard (Ternary + Function)
// Task:
// function loginStatus(isLoggedIn)

// Return:

// "Welcome back"
// "Please login"

// 👉 Must use ternary



// function loginStatus(isLoggedIn) {
//   return isLoggedIn ? "Welcome back" : "Please login";
// }

// console.log(loginStatus(true));
// console.log(loginStatus(false));



// 🧪 Problem 7 — Product Category System (Arrow + Function Call)
// Step 1:
// const getCategory = (price)
// Step 2:
// function labelProduct(product)
// Rule:
// < 20 → Budget
// 20–100 → Standard
// 100 → Premium

// const getCategory = (price) => {
//   if (price < 20) {
//     return 'Budget';
//   } else if (price <= 100) {
//     return 'Standard';
//   } else if (price > 100) {
//     return 'Standard';
//   } else {
//     return 'Invalid';
//   };
// };

// function labelProduct(product) {
//   return `${product.name} is ${getCategory(product.price)} product.`
// };

// const product = {
//   name: 'Shirt',
//   price: 100
// };

// console.log(labelProduct(product));



// 🧪 Problem 8 — Number Analyzer (Loop + Logic)
// Task:
// function analyzeNumbers(numbers)

// Input:

// [1, 2, 3, 4, 5]
// Output:
// {
//   even: 2,
//   odd: 3,
//   sum: 15
// }

// function analyzeNumbers(numbers) {
//   let sum = 0;
//   let evenCount = 0;
//   let oddCount = 0;
//   for (let i = 0; i < numbers.length; i++) {
//     sum = sum + numbers[i];
//   };
//   for (let i = 0; i < numbers.length; i++) {
//     if (numbers[i] % 2 === 0) {
//       evenCount++;
//     } else {
//       oddCount++;
//     }
//   };
//   return {
//     even: evenCount,
//     odd: oddCount,
//     sum: sum
//   };
// };

// const numbers = [1, 2, 3, 4, 5];
// console.log(analyzeNumbers(numbers));


// 🧪 Problem 9 — Password Validator (Multi Condition)
// Rules:
// < 6 → Weak
// 6–10 → Medium

// > 10 → Strong

// Empty → Invalid


// function passwordValidator(password) {
//   if (password.length > 0 && password.length < 6) {
//     return 'weak';
//   } else if (password.length >= 6 && password.length <= 10) {
//     return 'Medium';
//   } else if (password.length > 10) {
//     return 'Strong';
//   } else {
//     return 'Invalid';
//   };
// };

// console.log(`Your password is ${passwordValidator('213242')}`);


// 🧪 Problem 1 — Discount Service (Function Composition Core)
// Scenario:

// You are building a pricing engine for a store.

// Requirements:
// Step 1:

// Create a function declaration:

// getDiscountRate(price)

// Rules:

// ≥ 100 → 20%
// ≥ 50 → 10%
// else → 0%
// Step 2:

// Create another function:

// calculateDiscount(price)

// 👉 This function MUST:

// Call getDiscountRate
// Return the discount amount (not rate)
// Step 3:

// Create:

// finalPrice(price)

// 👉 MUST:

// Call calculateDiscount
// Return final price after discount
// ⚠️ Guidance:
// Separate responsibilities (rate vs amount vs final)
// No console.log inside functions


// function getDiscountRate(price) {
//   if (price >= 100) {
//     return 0.2;
//   } else if (price >= 50) {
//     return 0.1;
//   } else {
//     return 0;
//   };
// };

// function calculateDiscount(price) {
//   return getDiscountRate(price) * price;
// };

// function finalPrice(price) {
//   return price - calculateDiscount(price);
// };

// console.log(`Your final price is ${finalPrice(50)}`);


// 🧪 Problem 2 — User Greeting System (Function Expression)
// Scenario:

// You are generating greeting messages for users.

// Task:

// Create a function expression:

// const greetUser = function(user)

// Input:

// { name: "john", isLoggedIn: true }
// Rules:
// Capitalize name
// If logged in → "Welcome back, John"
// Else → "Please login, John"
// ⚠️ Guidance:
// Use ternary inside function
// Focus on string formatting


// const greetUser = function(user){
//   const capitalizedName = user.name[0].toUpperCase() + user.name.slice(1);
//   return user.name === 'john' && user.isLoggedIn == true ? `"Welcome back, ${capitalizedName}"` : `"Please login, ${capitalizedName}"`;
// }

// const user = {
//   name: "john",
//   isLoggedIn: true
// }

// console.log(greetUser(user));


// 🧪 Problem 3 — Payment Status Handler (Switch + Function)
// Scenario:

// Handle payment states in checkout

// Task:
// function handlePayment(status)
// Input values:
// "pending"
// "paid"
// "failed"
// "refunded"
// Output:
// Return meaningful messages (not generic)
// ⚠️ Guidance:
// Use switch
// Return directly from cases (no variables)

// function handlePayment(status) {
//   switch (status) {
//     case  "pending":
//       return `Your payment is being processed. Please wait.`;
//     case  "paid":
//       return `Payment successful! Your order is confirmed.`;
//     case  "failed":
//       return `Payment failed. Please try again or use a different method.`;
//     case  "refunded":
//       return `Your payment has been refunded successfully.`;
//     default:
//       return `Unknown payment status. Please contact support.`;
//   };
// };

// console.log(handlePayment("dwsws"));


// 🧪 Problem 4 — Access Control System (Arrow + Function Call)
// Scenario:

// You are controlling access to a dashboard.

// Step 1:

// Create an arrow function:

// const getRoleLevel = (role)

// Rules:

// "admin" → 3
// "editor" → 2
// "user" → 1
// others → 0
// Step 2:

// Create:

// function canEdit(role)

// 👉 MUST:

// Call getRoleLevel
// Return true if level ≥ 2
// ⚠️ Guidance:
// This is logic chaining
// Think in numbers, not strings


// const getRoleLevel = (role) => {
//   if (role === "admin") {
//     return 3;
//   } else if (role === "editor") {
//     return 2;
//   } else if (role === "user") {
//     return 1;
//   } else {
//     return 0;
//   };
// };

// function canEdit(role) {
//   return getRoleLevel(role) >= 2;
// };

// console.log(canEdit("admin"));




// 🧪 Problem 5 — Product Label Formatter (Mixed Functions)
// Scenario:

// You need to display product labels.

// Step 1 (Arrow):
// getCategory(price)

// Rules:

// < 20 → Budget
// 20–100 → Standard

// 100 → Premium

// Step 2 (Function Declaration):
// formatProduct(product)

// Input:

// { name: "bag", price: 120 }

// Output:

// "Bag is a Premium product"
// ⚠️ Guidance:
// Capitalize product name
// Call getCategory inside

// const getCategory = (price) => {
//   if (price < 20 && price > 0) {
//     return "Budget";
//   } else if (price >= 20 && price < 100 ) {
//     return "Standard";
//   } else if (price >= 100) {
//     return "Premium";
//   } else {
//     return "Invalid";
//   };
// };

// const product = { name: "bag", price: 120 };

// function formatProduct(product) {
//   const capitalizedName = product.name[0].toUpperCase() + product.name.slice(1);
//   return `${capitalizedName} is a ${getCategory(product.price)} product`;
// };

// console.log(formatProduct(product));


// 🧪 Problem 6 — Password Strength Service
// Scenario:

// Validate password strength

// Task:
// function getPasswordStrength(password)
// Rules:
// Empty → "Invalid"
// < 6 → "Weak"
// 6–10 → "Medium"

// 10 → "Strong"

// ⚠️ Guidance:
// Order conditions carefully
// Avoid unnecessary checks

// function getPasswordStrength(password) {
//   const passwordLength = password.length;
//   if (passwordLength === 0) {
//     return "Invalid";
//   } else if (passwordLength < 6) {
//     return "Weak";
//   } else if (passwordLength <= 10) {
//     return "Medium";
//   } else {
//     return "Strong";
//   };
// };

// console.log(`Your password is ${getPasswordStrength("1234567")}`);


// 🧪 Problem 7 — Shipping Calculator (Ternary Focus)
// Scenario:

// Calculate shipping cost

// Task:
// const getShipping = (price)
// Rules:
// ≥ 100 → 0
// else → 10
// ⚠️ Constraint:

// 👉 Must use ternary only


// const getShipping = (price) => {
//   return price >= 100 ? 0 : 10;
// }

// console.log(`Your shipping cost is ${getShipping(99)}`);



// 🧪 Problem 8 — Order Validator + Processor
// Scenario:

// You must validate and process orders

// Step 1:
// function isValidOrder(amount)

// Rules:

// amount > 0 → true
// else → false
// Step 2:
// function processOrder(amount)

// 👉 MUST:

// Call isValidOrder
// If invalid → "Invalid order"
// Else → "Order processed successfully"
// ⚠️ Guidance:
// Early return pattern


// function isValidOrder(amount) {
//   return amount > 0;
// }

// function processOrder(amount) {
//   if (isValidOrder(amount)) {
//     return `Order processed successfully`;
//   } else {
//     return `Invalid order`;
//   };
// };

// console.log(processOrder(0));



// 🧪 Problem 9 — Username Cleaner (Function Expression)
// Task:
// const cleanUsername = function(name)
// Rules:
// Remove spaces
// Convert to lowercase
// If empty → "invalid_user"
// ⚠️ Guidance:
// Use .trim() and .toLowerCase()

// const cleanUsername = function(name) {
//   const formatName = name.toLowerCase().trim();
//   return formatName.length === 0 ? "invalid_user" : formatName;
// };

// console.log(cleanUsername("john Doe"));


// 🧪 Problem 10 — Mini Pricing Engine (FINAL TEST)
// Scenario:

// Combine everything

// Task:
// function pricingEngine(price)
// Requirements:
// Use:
// discount logic
// shipping logic
// Return:
// {
//   price: 120,
//   discount: 24,
//   shipping: 0,
//   final: 96
// }
// ⚠️ Guidance:
// Call other functions you already built
// DO NOT rewrite logic again

// 👉 This tests function reuse


// function getDiscountRate(price) {
//   if (price >= 100) {
//     return 0.2;
//   } else if (price >= 50) {
//     return 0.1;
//   } else {
//     return 0;
//   };
// };

// function calculateDiscount(price) {
//   return getDiscountRate(price) * price;
// };

// function finalPrice(price) {
//   return price - calculateDiscount(price);
// };

// const getShipping = (price) => {
//   return price >= 100 ? 0 : 10;
// }

// function pricingEngine(price) {
//   return {
//     price: price,
//     discount: calculateDiscount(price),
//     shipping: getShipping(price),
//     final: finalPrice(price)
//   }
// }

// console.log(pricingEngine(120));



// 🎮 DRILL SET — “FUN BUT PROFESSIONAL”

// Think of this as mini real-world features, not textbook problems.

// 🧪 Problem 1 — “Netflix Password Drama”
// Scenario:

// A streaming app is checking account usage.

// Task:

// Create:

// function checkAccount(isLoggedIn, isSubscribed)
// Rules:
// If not logged in → "Please log in"
// If logged in but not subscribed → "Buy a subscription"
// If both true → "Enjoy your movie 🍿"
// ⚠️ Guidance:
// Use if/else only
// Order matters

// function checkAccount(isLoggedIn, isSubscribed) {
//   if (isLoggedIn && isSubscribed) {
//     return "Enjoy your movie 🍿";
//   } else if (isLoggedIn && isSubscribed === false) {
//     return "Buy a subscription";
//   } else {
//     return "Please log in"
//   };
// };

// console.log(checkAccount(false, false));

// 🧪 Problem 2 — “Pizza Order Mood”
// Scenario:

// User mood affects message.

// Task:
// const pizzaMessage = (mood)
// Rules:
// "happy" → "Extra cheese for you!"
// "sad" → "Free dessert added ❤️"
// anything else → "Standard order"
// ⚠️ Constraint:

// 👉 Must use switch

// const pizzaMessage = (mood) => {
//   switch (mood) {
//     case "happy":
//       return "Extra cheese for you!";
//     case "sad":
//       return "Free dessert added.";
//     default:
//       return "Standard order";
//   };
// };
// console.log(pizzaMessage("sad"));


// 🧪 Problem 3 — “Username Judge”
// Scenario:

// You reject bad usernames.

// Task:
// const validateUsername = function(name)
// Rules:
// empty → "Invalid"
// length < 3 → "Too short"
// includes space → "No spaces allowed"
// else → "Valid username"
// ⚠️ Guidance:
// Use .includes(" ")
// Order checks carefully

// const validateUsername = function(name) {
//   if (name.length === 0) {
//     return "Invalid";
//   } else if (name.length < 3) {
//     return "Too short";
//   } else if (name.includes(" ")) {
//     return "No spaces allowed";
//   } else {
//     return "Valid username";
//   };
// };

// console.log(validateUsername(""));


// 🧪 Problem 4 — “Discount Boss Fight”
// Scenario:

// User tries to apply a coupon.

// Step 1:
// function getCouponValue(code)

// Rules:

// "KING" → 50
// "SAVE10" → 10
// else → 0
// Step 2:
// function applyCoupon(price, code)

// 👉 MUST:

// Call getCouponValue
// Return final price after discount
// ⚠️ Twist:

// If discount > price → final = 0 (no negative prices)


// function getCouponValue(code) {
//   if (code === "KING") {
//     return 50;
//   } else if (code === "SAVE10") {
//     return 10;
//   } else {
//     return 0;
//   };
// };


// function applyCoupon(price, code) {
//   const discount = getCouponValue(code);
//   return discount > price ? 0 : price - discount;
// }

// console.log(`Final price after discount is ${applyCoupon(49, "KING")}$`);


// 🧪 Problem 5 — “Smart Reply Bot 🤖”
// Scenario:

// Auto-reply system

// Task:
// function autoReply(message)
// Rules:
// "hi" → "Hello!"
// "bye" → "Goodbye!"
// "help" → "How can I assist?"
// anything else → "I don't understand"
// ⚠️ Constraint:

// 👉 Use switch


// function autoReply(message) {
//   switch (message) {
//     case 'hi':
//       return "Hello!";
//     case 'bye':
//       return "GoodBye!";
//     case 'help':
//       return "How can I assist?";
//     default:
//       return "I don't understand";
//   };
// };

// console.log(autoReply("hi"));


// 🧪 Problem 6 — “VIP Access Gate”
// Scenario:

// Club entry system

// Task:
// function canEnter(age, isVIP)
// Rules:
// VIP → always allowed
// age ≥ 18 → allowed
// else → denied
// ⚠️ Guidance:
// Use ternary inside return

// function canEnter(age, isVIP) {
//   return isVIP ? "Always Allowed" : age >= 18 ? "Allowed" : "Denied";
// }

// console.log(canEnter(8, true));


// 🧪 Problem 7 — “Product Hype Generator”
// Scenario:

// Marketing label generator

// Step 1:
// const getHypeLevel = (price)

// Rules:

// < 20 → "Cheap"
// 20–100 → "Hot Deal"

// 100 → "Luxury"

// Step 2:
// function generateAd(product)

// Input:

// { name: "watch", price: 150 }

// Output:
// 👉 "This Watch is a Luxury item!"

// ⚠️ Guidance:
// Capitalize name
// Call function inside

// const getHypeLevel = (price) => {
//   if (price >= 100) {
//     return "Luxury";
//   } else if (price >= 20 ) {
//     return "Hot Deal";
//   } else if (price < 20) {
//     return "Cheap";
//   } else {
//     return "Invalid";
//   };
// };

// function generateAd(product) {
//   const capitalizedProductName = product.name[0].toUpperCase() + product.name.slice(1);
//   return `This ${capitalizedProductName} is a ${getHypeLevel(product.price)} item!`;
// }

// const product = {
//   name: "watch",
//   price: 120
// }

// console.log(generateAd(product));




// 🧪 Problem 8 — “Login Attempt Tracker”
// Scenario:

// Security system

// Task:
// function loginAttempt(attempts)
// Rules:
// 0 → "No attempts yet"
// 1–3 → "Be careful"

// 3 → "Account locked"

// ⚠️ Guidance:
// Use clean conditions (avoid over-complication)

// function loginAttempt(attempts) {
//   if (attempts < 1) {
//     return "No attempts yet";
//   } else if (attempts >= 1 && attempts < 3) {
//     return "Be careful";
//   } else {
//     return "Account locked";
//   };
// };

// console.log(loginAttempt(2));




// 🧪 Problem 9 — “Temperature Advisor 🌡️”
// Scenario:

// Weather app suggestion

// Task:
// const weatherAdvice = (temp)
// Rules:
// < 10 → "Wear a jacket"
// 10–25 → "Nice weather"

// 25 → "Stay hydrated"

// ⚠️ Constraint:

// 👉 Use ternary (nested allowed)


// const weatherAdvice = (temp) => {
//   return temp < 10 ? "Wear A jacket" : temp >= 10 && temp < 25 ? "Nice weather" : "Stay hydrated";
// }

// console.log(weatherAdvice(7));



// 🧪 Problem 10 — “Mini Game Score System 🕹️”
// Scenario:

// Game scoring logic

// Step 1:
// function getScoreLevel(score)

// Rules:

// ≥ 90 → "Pro"
// ≥ 50 → "Average"
// else → "Noob"
// Step 2:
// function getGameResult(score)

// 👉 MUST:

// Call getScoreLevel
// Return:
// {
//   score: 80,
//   level: "Average"
// }


// function getScoreLevel(score) {
//   if (score >= 90) {
//     return "Pro";
//   } else if (score >= 50) {
//     return "Average";
//   } else {
//     return "Noob";
//   };
// };

// function getGameResult(score) {
//   const scoreLevel = getScoreLevel(score);
//   return {
//     score: score,
//     level: scoreLevel
//   };
// };

// console.log(getGameResult(80));



// 🧪 Problem 1 — Product Object (CORE)
// Task:

// Create:

// const product = {
//   name: "shoes",
//   price: 100,
// }
// Add method:
// getLabel()

// 👉 Output:

// "Shoes costs $100"
// ⚠️ Rules:
// Use this
// Capitalize name


// const product = {
//   name: "shoes",
//   price: 100,
//   getLabel: function() {
//     const capitalizedProductName = this.name[0].toUpperCase() + this.name.slice(1);
//     return `${capitalizedProductName} costs $${this.price}`;
//   };
// };

// console.log(product.getLabel());


// 🧪 Problem 2 — Discounted Product
// Add method:
// getDiscountedPrice()
// Rules:
// If price ≥ 100 → 20% discount
// else → no discount
// ⚠️ Use:
// this.price

// const product = {
//   name: "shoes",
//   price: 100,
//   getDiscountedPrice: function() {
//     const discountRate = this.price >= 100 ? 0.2 : 0;
//     const discountAmount = this.price * discountRate;
//     return this.price - discountAmount;
//   }
// };

// console.log(`${product.getDiscountedPrice()}$`);


// 🧪 Problem 3 — User Object
// Task:
// const user = {
//   name: "john",
//   isLoggedIn: true,
// }
// Add:
// getMessage()
// Rules:
// logged in → "Welcome John"
// else → "Please login John"

// const user = {
//   name: "john",
//   isLoggedIn: true,
//   getMessage: function() {
//     const capitalizedUserName = this.name[0].toUpperCase() + this.name.slice(1);
//     return this.isLoggedIn ? `Welcome ${capitalizedUserName}` : `Please login ${capitalizedUserName}`;
//   }
// };

// console.log(user.getMessage());


// 🧪 Problem 4 — Cart Object (REAL THINKING)
// const cart = {
//   price: 120,
//   shipping: 10,
// }
// Add:
// getFinalPrice()

// 👉 Output:

// 110 (if discount applied)
// or
// 130 (if no discount)
// Rule:
// If price ≥ 100 → 10% discount

// const cart = {
//   price: 120,
//   shipping: 10,
//   getFinalPrice: function() {
//     if (this.price >= 100) {
//       const discountRate = 0.1 * this.price;
//       return (this.price - discountRate) + this.shipping;
//     } else {
//       return this.price + this.shipping;
//     };
//   }
// };

// console.log(cart.getFinalPrice());



// 🧪 Problem 5 — Array + Object (LIGHT INTRO)
// const products = [
//   { name: "shirt", price: 50 },
//   { name: "shoes", price: 120 }
// ];
// Task:

// 👉 Access:

// First product name
// Second product price

// 👉 No loops yet
// 👉 Just indexing

// const products = [
//   { name: "shirt", price: 50 },
//   { name: "shoes", price: 120 }
// ];

// console.log(products[0].name);
// console.log(products[1].price);


// 🧪 Problem 6 — Smart Product Object

// Create object with:

// {
//   name,
//   price,
//   comparePrice
// }
// Add method:
// isOnSale()

// 👉 Rule:
// Return true if:

// price < comparePrice

// const product = {
//   name: "shirt",
//   price: 100,
//   comparePrice: 110,
//   isOnSale() {
//     return this.price < this.comparePrice;
//   }
// };

// console.log(product.isOnSale());


// 🧪 Problem 7 — Order Object
// const order = {
//   product: "Bag",
//   price: 100,
//   status: "paid"
// }
// Add:
// getStatusMessage()
// Use switch:
// paid → "Order confirmed"
// pending → "Waiting for payment"
// failed → "Payment failed"

// const order = {
//   product: "Bag",
//   price: 100,
//   status: "paid",
//   getStatusMessage() {
//     switch (this.status) {
//       case 'paid':
//         return "Order confirmed";
//       case 'pending':
//         return "Waiting for payment";
//       case 'failed':
//         return "Payment failed";
//       default:
//         return "Invalid Status";
//     }
//   }
// };

// console.log(order.getStatusMessage());


// 🧪 Problem 8 — Profile Object (this mastery)
// const profile = {
//   firstName: "john",
//   lastName: "doe"
// }
// Add:
// getFullName()

// 👉 Output:

// "John Doe"

// const profile = {
//   firstName: "john",
//   lastName: "doe",
//   getFullName() {
//     const capitalizedFirstName = this.firstName[0].toUpperCase() + this.firstName.slice(1);
//     const capitalizedLastName = this.lastName[0].toUpperCase() + this.lastName.slice(1);
//     return `${capitalizedFirstName} ${capitalizedLastName}`;
//   }
// };

// console.log(profile.getFullName());


// 🚀 SYSTEM DRILL — “SMART ORDER ENGINE (SHOPIFY-LIKE)”

// You’re going to build a connected system, not isolated functions.

// 🧠 SYSTEM GOAL

// You will simulate:

// 👉 User
// 👉 Product
// 👉 Order
// 👉 Pricing Logic
// 👉 Messaging

// Everything connected.

// ⚠️ STRICT RULES
// ❌ No global variables
// ❌ No console.log inside logic functions
// ❌ No repeated logic
// ✅ Use this in object methods
// ✅ Functions must call other functions
// ✅ Return structured data
// 🧩 PART 1 — USER OBJECT
// const user = {
//   name: "john",
//   isLoggedIn: true,
//   isPremium: false
// }
// Add method:
// getStatus()
// Rules:
// Not logged in → "Guest"
// Logged in + premium → "Premium User"
// Logged in only → "Regular User"
// ⚠️ Guidance:

// 👉 Use this
// 👉 No external variables

// 🧩 PART 2 — PRODUCT OBJECT
// 🧪 Task:
// const product = {
//   name: "bag",
//   price: 120,
//   comparePrice: 150
// }
// Add methods:
// 1.
// getCategory()

// Rules:

// < 20 → "Budget"
// 20–100 → "Standard"

// 100 → "Premium"

// 2.
// isOnSale()

// Rule:

// price < comparePrice
// 3.
// getLabel()

// Output:

// "Bag (Premium)"
// 🧩 PART 3 — PRICING ENGINE (FUNCTIONS)
// 🧪 Task:
// Step 1:
// function getDiscountRate(userStatus)

// Rules:

// Premium User → 20%
// Regular User → 10%
// Guest → 0%
// Step 2:
// function calculateDiscount(price, userStatus)

// 👉 Must call getDiscountRate

// Step 3:
// const getShipping = (price, userStatus)

// Rules:

// Premium → Free
// price ≥ 100 → Free
// else → 10

// ⚠️ Return numbers only (0 or 10)

// 🧩 PART 4 — ORDER OBJECT (CORE)
// 🧪 Task:

// Create:

// const order = {
//   user: user,
//   product: product
// }
// Add method:
// getSummary()
// MUST DO inside:
// Get user status → this.user.getStatus()
// Get product category → this.product.getCategory()
// Get discount → call function
// Get shipping → call function
// Calculate final price
// Output:
// {
//   user: "john",
//   status: "Regular User",
//   product: "Bag",
//   category: "Premium",
//   price: 120,
//   discount: 12,
//   shipping: 0,
//   final: 108
// }
// 🧩 PART 5 — MESSAGE GENERATOR
// 🧪 Task:

// Add method inside order:

// getMessage()
// Output:

// 👉

// "John (Regular User) ordered a Premium Bag. Final: $108"
// ⚠️ Guidance:
// Use data from getSummary()
// Capitalize name + product

// const user = {
//   name: "john",
//   isLoggedIn: true,
//   isPremium: false,
//   getStatus() {
//     if (this.isLoggedIn && this.isPremium) {
//       return "Premium User"
//     } else if (this.isLoggedIn && !this.isPremium) {
//       return "Regular User"
//     } else {
//       return "Guest"
//     }
//   }
// };

// const product = {
//   name: "bag",
//   price: 120,
//   comparePrice: 150,
//   getCategory() {
//     if (this.price >= 100) {
//       return "Premium";
//     } else if (this.price >= 20) {
//       return "Standrd";
//     } else {
//       return "Budget";
//     }
//   },
//   isOnSale() {
//     return this.price < this.comparePrice;
//   }
// };

// function getDiscountRate(userStatus) {
//   if (userStatus === "Premium User") {
//     return 0.2;
//   } else if (userStatus === "Regular User") {
//     return 0.1;
//   } else {
//     return 0;
//   };
// };

// function calculateDiscount(price, userStatus) {
//   return price * getDiscountRate(userStatus);
// };

// const getShipping = (price, userStatus) => {
//   if (userStatus === "Premium User" || price >= 100) {
//     return 0;
//   } else {
//     return 10;
//   };
// };

// const order = {
//   user: user,
//   product: product,
//   getSummary() {
//     const capitializeUserName = this.user.name[0].toUpperCase() + this.user.name.slice(1);
//     const capitializeProductName = this.product.name[0].toUpperCase() + this.product.name.slice(1);
//     const userStatus = this.user.getStatus();
//     const productCategory = this.product.getCategory();
//     const discount = calculateDiscount(this.product.price, userStatus);
//     const shipping = getShipping(this.product.price, userStatus);
//     const finalPrice = (this.product.price - discount) + shipping;
//     return {
//       name: capitializeUserName,
//       status: userStatus,
//       product: capitializeProductName,
//       category: productCategory,
//       price: this.product.price,
//       discount: discount,
//       shipping: shipping,
//       final: finalPrice
//     };
//   },
//   getMessage() {
//     return `${this.getSummary().name} (${this.getSummary().status}) ordered a ${this.getSummary().category} ${this.getSummary().product}. Final: ${this.getSummary().final}$`;
//   }
// };

// console.log(order.getMessage());




// 🚀 SYSTEM DRILL — “SMART ORDER + USER PERKS ENGINE”
// 🧠 WHAT YOU’RE BUILDING

// A system that:

// Understands user type
// Applies special perks
// Handles product pricing
// Generates a clean business message

// 👉 This is closer to real production logic

// 🧩 SYSTEM STRUCTURE

// You will build:

// User Object (with behavior)
// Product Object (with behavior)
// Perk Engine (functions)
// Order Object (orchestrator)
// 🧩 PART 1 — USER SYSTEM (OBJECT + METHOD)
// 🧪 Task

// Create:

// const user = {
//   name: "john",
//   isLoggedIn: true,
//   isPremium: true
// }
// 👉 Add method:
// getStatus()
// Rules:
// Not logged in → "Guest"
// Logged in + premium → "Premium User"
// Logged in only → "Regular User"
// 🎯 Extra Thinking:

// Add another method:

// getDiscountBonus()
// Rules:
// Premium → extra 5% discount
// Others → 0
// 🧩 PART 2 — PRODUCT SYSTEM (OBJECT + METHODS)
// 🧪 Task
// const product = {
//   name: "headphones",
//   price: 80,
//   comparePrice: 120
// }
// 👉 Add methods:
// 1. getCategory()

// Rules:

// < 20 → Budget
// 20–100 → Standard

// 100 → Premium

// 2. getSavings()

// 👉 Return:

// comparePrice - price
// 3. getLabel()

// 👉 Output:

// "Headphones (Standard)"
// 🧩 PART 3 — PRICING ENGINE (FUNCTIONS)
// 🧪 Task 1
// function getBaseDiscount(price)

// Rules:

// ≥ 100 → 20%
// ≥ 50 → 10%
// else → 0
// 🧪 Task 2
// function calculateFinalDiscount(price, user)

// 👉 MUST:

// Call getBaseDiscount
// Add user bonus discount
// Return final discount amount
// 🧪 Task 3
// const getShipping = (price, user)

// Rules:

// Premium → Free
// price ≥ 100 → Free
// else → 10

// 👉 Use ternary

// 🧩 PART 4 — ORDER SYSTEM (MAIN OBJECT)
// 🧪 Task

// Create:

// const order = {
//   user,
//   product,
// }
// 👉 Add method:
// getSummary()
// 🔥 INSIDE THIS METHOD YOU MUST:
// Get user status
// Get product label
// Calculate discount
// Get shipping
// Calculate final price
// 👉 Output EXACT STRUCTURE:
// {
//   user: "John",
//   status: "Premium User",
//   product: "Headphones",
//   category: "Standard",
//   savings: 40,
//   price: 80,
//   discount: 12,
//   shipping: 0,
//   final: 68
// }
// 🧩 PART 5 — MESSAGE ENGINE (REAL UX)
// 🧪 Task

// Add:

// getMessage()
// 👉 Output:
// "John (Premium User) saved $40 on a Standard Headphones. Final price: $68"



// 🚀 SYSTEM DRILL — “SMART ORDER + USER PERKS ENGINE”
// 🧠 WHAT YOU’RE BUILDING

// A system that:

// Understands user type
// Applies special perks
// Handles product pricing
// Generates a clean business message

// 👉 This is closer to real production logic

// 🧩 SYSTEM STRUCTURE

// You will build:

// User Object (with behavior)
// Product Object (with behavior)
// Perk Engine (functions)
// Order Object (orchestrator)
// 🧩 PART 1 — USER SYSTEM (OBJECT + METHOD)
// 🧪 Task

// Create:

// const user = {
//   name: "john",
//   isLoggedIn: true,
//   isPremium: true
// }
// 👉 Add method:
// getStatus()
// Rules:
// Not logged in → "Guest"
// Logged in + premium → "Premium User"
// Logged in only → "Regular User"
// 🎯 Extra Thinking:

// Add another method:

// getDiscountBonus()
// Rules:
// Premium → extra 5% discount
// Others → 0
// 🧩 PART 2 — PRODUCT SYSTEM (OBJECT + METHODS)
// 🧪 Task
// const product = {
//   name: "headphones",
//   price: 80,
//   comparePrice: 120
// }
// 👉 Add methods:
// 1. getCategory()

// Rules:

// < 20 → Budget
// 20–100 → Standard

// 100 → Premium

// 2. getSavings()

// 👉 Return:

// comparePrice - price
// 3. getLabel()

// 👉 Output:

// "Headphones (Standard)"
// 🧩 PART 3 — PRICING ENGINE (FUNCTIONS)
// 🧪 Task 1
// function getBaseDiscount(price)

// Rules:

// ≥ 100 → 20%
// ≥ 50 → 10%
// else → 0
// 🧪 Task 2
// function calculateFinalDiscount(price, user)

// 👉 MUST:

// Call getBaseDiscount
// Add user bonus discount
// Return final discount amount
// 🧪 Task 3
// const getShipping = (price, user)

// Rules:

// Premium → Free
// price ≥ 100 → Free
// else → 10

// 👉 Use ternary

// 🧩 PART 4 — ORDER SYSTEM (MAIN OBJECT)
// 🧪 Task

// Create:

// const order = {
//   user,
//   product,
// }
// 👉 Add method:
// getSummary()
// 🔥 INSIDE THIS METHOD YOU MUST:
// Get user status
// Get product label
// Calculate discount
// Get shipping
// Calculate final price
// 👉 Output EXACT STRUCTURE:
// {
//   user: "John",
//   status: "Premium User",
//   product: "Headphones",
//   category: "Standard",
//   savings: 40,
//   price: 80,
//   discount: 12,
//   shipping: 0,
//   final: 68
// }
// 🧩 PART 5 — MESSAGE ENGINE (REAL UX)
// 🧪 Task

// Add:

// getMessage()
// 👉 Output:
// "John (Premium User) saved $40 on a Standard Headphones. Final price: $68"

// const userStatus = {
//   GUEST: "Guest",
//   REGULAR: "Regular User",
//   PREMIUM: "Premium User"
// };

// const user = {
//   name: "john",
//   isLoggedIn: true,
//   isPremium: true,
//   getStatus() {
//     if (this.isLoggedIn && this.isPremium) {
//       return userStatus.PREMIUM;
//     };
//     if (this.isLoggedIn && !this.isPremium) {
//       return userStatus.REGULAR;
//     }
//     return userStatus.GUEST;
//   },
//   capitalizedUserName() {
//     return this.name[0].toUpperCase() + this.name.slice(1);
//   },
//   getDiscountBonus() {
//     return this.getStatus() === userStatus.PREMIUM ? 0.05 : 0;
//   }
// };

// const productCategories = {
//   PREMIUM: "Premium",
//   STANDARD: "Standard",
//   BUDGET: "Budget"
// };

// const product = {
//   name: "headphones",
//   price: 80,
//   comparePrice: 120,
//   getCategory() {
//     if (this.price >= 100) {
//       return productCategories.PREMIUM;
//     };
//     if (this.price >= 20 ) {
//       return productCategories.STANDARD;
//     };
//     return productCategories.BUDGET;
//   },
//   getSavings() {
//     return this.comparePrice - this.price;
//   },
//   capitalizedProductName() {
//     return this.name[0].toUpperCase() + this.name.slice(1);
//   },
//   getLabel() {
//     return `${this.capitalizedProductName()} (${this.getCategory()})`;
//   }
// };

// function getBaseDiscount(price) {
//   if (price >= 100) {
//     return 0.2;
//   };
//   if (price >= 50) {
//     return 0.1;
//   };
//   return 0;
// };

// function calculateFinalDiscount(price, user) {
//   const base = price * getBaseDiscount(price);
//   const bonus = price * user.getDiscountBonus();
//   return base + bonus;
// }

// const getShipping = (price, user) => {
//   return user.getStatus() === userStatus.PREMIUM || price >= 100 ? 0 : 10;
// };

// const order = {
//   user,
//   product,

//   getSummary() {
//     const userStatusValue = this.user.getStatus();
//     const productLabel = this.product.capitalizedProductName();
//     const category = this.product.getCategory();

//     const savings = this.product.getSavings();
//     const price = this.product.price;

//     const discount = calculateFinalDiscount(price, this.user);
//     const shipping = getShipping(price, this.user);

//     const final = price - discount + shipping;

//     return {
//       user: this.user.capitalizedUserName(),
//       status: userStatusValue,
//       product: productLabel,
//       category,
//       savings,
//       price,
//       discount,
//       shipping,
//       final
//     };
//   },

//   getMessage() {
//     const s = this.getSummary();
//     return `${s.user} (${s.status}) saved $${s.savings} on a ${s.category} ${s.product}. Final price: $${s.final}`;
//   }
// };

// console.log(order.getMessage());


// 🧪 Problem 1 — Cart Total (Arrays + Loop + If)
// Scenario:

// You have product prices.

// Task:
// function getCartTotal(prices)
// Input:
// [10, 20, 30]
// Output:
// 60
// Rules:
// Use a loop
// Ignore negative numbers

// function getCartTotal(prices) {
//   let cartTotal = 0;
//   for (let i = 0; i <= (prices.length - 1); i++) {
//     if (prices[i] > 0) {
//       cartTotal = cartTotal + prices[i];
//     };
//   };
//   return cartTotal;
// };

// console.log(getCartTotal([10, 20, 30]));


// 🧪 Problem 2 — Count Premium Products
// Scenario:

// Products above 100 are premium.

// Task:
// function countPremium(products)
// Input:
// [
//   { name: "shirt", price: 50 },
//   { name: "shoes", price: 120 }
// ]
// Output:
// 1
// Rules:
// Use loop
// Use condition inside loop

// const products = [
//   { name: "shirt", price: 200 },
//   { name: "shoes", price: 120 }
// ];

// function countPremium(products) {
//   let productCount = 0;
//   for (let i = 0; i < products.length; i++) {
//     if (products[i].price > 100) {
//       productCount++;
//     };
//   };
//   return productCount;
// };

// console.log(countPremium(products));

// 🧪 Problem 3 — Extract Affordable Products
// Task:
// function getAffordable(products)
// Rule:
// Return products where price < 50

// ⚠️ Must return new array

// const products = [
//   { name: "shirt", price: 45 },
//   { name: "shoes", price: 20 }
// ];

// function getAffordable(products) {
//   let affordableProducts = [];
//   for (let i = 0; i < products.length; i++) {
//     if (products[i].price < 50) {
//       affordableProducts.push(products[i].name);
//     };
//   };
//   return affordableProducts;
// };

// console.log(getAffordable(products));


// 🧪 Problem 4 — Order Status Counter (Switch + Loop)
// Task:
// function countOrders(statuses)
// Input:
// ["paid", "pending", "paid", "failed"]
// Output:
// {
//   paid: 2,
//   pending: 1,
//   failed: 1
// }

// ⚠️ Use:

// loop
// switch

// const statuses = ["paid", "pending", "paid", "failed"];

// function countOrders(statuses) {
//   let paidCount = 0;
//   let pendingCount = 0;
//   let failedCount = 0;
//   for (let i = 0; i < statuses.length; i++) {
//     switch (statuses[i]) {
//       case "paid":
//         paidCount++;
//         break;
//       case "pending":
//         pendingCount++;
//         break;
//       case "failed":
//         failedCount++;
//         break;
//     }
//   };
//   return {
//     paid: paidCount,
//     pending: pendingCount,
//     failed: failedCount
//   };
// };

// console.log(countOrders(statuses));


// 🧪 Problem 5 — User Greeting System (Object + Method)
// Task:
// const user = {
//   name: "john",
//   isLoggedIn: true,
// }

// Add method:

// getMessage()
// Rules:
// Logged in → "Welcome John"
// else → "Please login John"

// const user = {
//   name: "john",
//   isLoggedIn: false,
//   capitalizedUserName() {
//     return this.name.charAt(0).toUpperCase() + this.name.slice(1);
//   },
//   getMessage() {
//     const userName = this.capitalizedUserName();
//     return this.isLoggedIn ? `Welcome ${userName}` : `Please login ${userName}`;
//   }
// };

// console.log(user.getMessage());

// 🧪 Problem 6 — Discount Calculator (Function + Loop)
// Task:
// function getTotalDiscount(prices)
// Rules:
// price ≥ 100 → 20%
// else → 0
// Return total discount

// const prices = [10, 101, 30];
// function getTotalDiscount(prices) {
//   let discounts = [];
//   for (let i = 0; i < prices.length; i++) {
//     if (prices[i] >= 100 ) {
//       discounts.push(0.2);
//     } else {
//       discounts.push(0);
//     };
//   };
//   return discounts;
// };

// console.log(getTotalDiscount(prices));

// 🧪 Problem 7 — Product Formatter (Array + Function Call)
// Task:
// function formatProducts(products)
// Output:
// ["Shirt - $50", "Shoes - $120"]

// ⚠️ Use:

// loop
// capitalization

// const products = [
//   { name: "shirt", price: 50 },
//   { name: "shoes", price: 120 }
// ];

// function formatProducts(products) {
//   let productInfo = [];
//   for (let i = 0; i < products.length; i++) {
//     let captitalizesProductName = products[i].name.charAt(0).toUpperCase() + products[i].name.slice(1);
//     productInfo.push(`${captitalizesProductName} - $${products[i].price}`);
//   };
//   return productInfo;
// };

// console.log(formatProducts(products));

// 🧪 Problem 8 — Stock Analyzer (Ternary + Loop)
// Task:
// function checkStock(stocks)
// Input:
// [0, 5, 20]
// Output:
// ["Out", "Low", "In"]
// Rules:
// 0 → Out
// 1–10 → Low

// 10 → In

// ⚠️ Use ternary

// const stocks = [0, 5, 20];

// function checkStock(stocks) {
//   let stockStatuses = [];
//   for (let i = 0; i < stocks.length; i++) {
//     stocks[i] > 10 ? stockStatuses.push('In') : stocks[i] < 1 ? stockStatuses.push('Out') : stockStatuses.push('Low');
//   };
//   return stockStatuses;
// };
// console.log(checkStock(stocks));

// 🧪 Problem 9 — Find Highest Price (Core Logic)
// Task:
// function getMax(prices)
// Input:
// [10, 99, 30]
// Output:
// 99

// ⚠️ Use loop (no Math.max)

// const prices = [10, 99, 30];

// function getMax(prices) {
//   let maxPrice = prices[0];
//   for (let i = 0; i < prices.length; i++) {
//     if (prices[i] > maxPrice ) {
//       maxPrice = prices[i];
//     };
//   };
//   return maxPrice;
// };

// console.log(getMax(prices));

// 🧪 Problem 10 — Mini Order Object (Everything Combined)
// Task:

// Create:

// const order = {
//   items: [
//     { name: "shirt", price: 50 },
//     { name: "shoes", price: 120 }
//   ],
// }

// Add method:

// getSummary()
// Must return:
// {
//   total: 170,
//   discount: 24,
//   final: 146
// }
// Rules:
// Loop through items
// Apply discount:
// ≥100 → 20%
// No global variables

// const order = {
//   items: [
//     { name: "shirt", price: 50 },
//     { name: "shoes", price: 120 }
//   ],
//   getTotal() {
//     let total = 0;
//     for (let i = 0; i < this.items.length; i++) {
//       total += this.items[i].price;
//     }
//     return total;
//   },
//   getTotalDiscounts() {
//     let discount = 0;
//     for (let i = 0; i < this.items.length; i++) {
//       if (this.items[i].price >= 100) {
//         discount += this.items[i].price * 0.2;
//       }
//     }
//     return discount;
//   },
//   getFinalPrice() {
//     return this.getTotal() - this.getTotalDiscounts();
//   },
//   getSummary() {
//     return {
//       total: this.getTotal(),
//       discount: this.getTotalDiscounts(),
//       final: this.getFinalPrice()
//     };
//   }
// };

// console.log(order.getSummary());

// ************************************************
// ************************************************
// *********** Task Description Start *************
// ************************************************
// ************************************************

// 🧩 SYSTEM STATE

// You will start with:

// const cart = {
//   user: {
//     name: "john",
//     isLoggedIn: true,
//     isPremium: false
//   },
//   items: [
//     { name: "shirt", price: 50 },
//     { name: "shoes", price: 150 },
//     { name: "cap", price: 30 }
//   ]
// };
// 🧪 PART 1 — CART TOTAL ENGINE
// Task:

// Create:

// function getCartTotal(items)
// Rules:
// Loop through items
// Return total price
// 🧪 PART 2 — ITEM LEVEL DISCOUNT (IMPORTANT)
// Task:
// function getItemDiscount(items)
// Rules:
// If item price ≥ 150 → 5% discount
// Else → 0
// Return total item discount

// 👉 This mimics Shopify line-item discounts

// 🧪 PART 3 — USER-BASED DISCOUNT
// Task:
// function getUserDiscount(total, user)
// Rules:
// Premium → 10%
// Regular → 5%
// Guest → 0

// 👉 Use user flags (isLoggedIn, isPremium)

// 🧪 PART 4 — CART LEVEL RULE
// Task:
// function getCartDiscount(total)
// Rule:
// If total ≥ 200 → $20 OFF

// 👉 Fixed discount (not percentage)

// 🧪 PART 5 — SHIPPING ENGINE
// Task:
// function getShipping(items, total)
// Rules:
// If items.length > 2 → FREE
// Else if total ≥ 100 → FREE
// Else → $10

// 👉 This is multi-condition logic

// 🧪 PART 6 — FINAL ENGINE (CORE)
// Task:
// function buildCartSummary(cart)
// MUST:

// Inside this function:

// Call all previous functions
// Combine results
// Output:
// {
//   total: 230,
//   itemDiscount: 7.5,
//   userDiscount: 23,
//   cartDiscount: 20,
//   shipping: 0,
//   final: 179.5
// }

// ************************************************
// ************************************************
// ************ Task Description End **************
// ************************************************
// ************************************************


const cart = {
  user: {
    name: "john",
    isLoggedIn: true,
    isPremium: false
  },
  items: [
    { name: "shirt", price: 50 },
    { name: "shoes", price: 150 },
    { name: "cap", price: 30 }
  ]
};

function getCartTotal(items) {
  let cartTotal = 0;
  for (let i = 0; i < items.length; i++) {
    cartTotal += items[i].price;
  };
  return cartTotal;
};

function getItemDiscount(items) {
  let discount = 0;
  for (let i = 0; i < items.length; i++) {
    if (items[i].price >= 150) {
      discount += items[i].price * 0.05;
    };
  };
  return discount;
};

const userStatus = {
  GUEST: "Guest",
  REGULAR: "Regular User",
  PREMIUM: "Premium User"
};

function getUserStatus(user) {
  if (user.isLoggedIn) {
    return userStatus.REGULAR;
  };
  if (user.isPremium) {
    return userStatus.PREMIUM;
  }
  return userStatus.GUEST;
};

function getUserDiscount(total, user) {
  const userStatus = getUserStatus(user);
  if (userStatus === userStatus.PREMIUM) {
    return 0.1;
  };
  if (userStatus === userStatus.REGULAR) {
    return 0.05;
  };
  return 0;
};
